/**
 * Shared panels: the tenant edit modal and the component panel.
 *
 * Both languages live side by side so a review sees the pair in one diff.
 * `index.ts` merges this module into the catalogs under the `panels` namespace.
 */
export const de = {
  tenantEdit: {
    notFound: 'Mandant nicht gefunden.',
    loadError: 'Fehler beim Laden.',
    saveError: 'Fehler beim Speichern.',
    loading: 'Mandantendaten werden geladen...',
    titlePrefix: 'Mandant bearbeiten:',
    changeSingular: 'Aenderung',
    changePlural: 'Aenderungen',
    close: 'Schliessen',
    tabsAriaLabel: 'Mandant-Einstellungen',
    tabs: {
      identity: 'Identitaet',
      apps: 'Apps',
      mail: 'Mail',
      iam: 'IAM',
      network: 'Netzwerk',
      storage: 'Storage',
      quota: 'Quota',
    },
    identity: {
      displayNameLabel: 'Anzeigename',
      displayNameHint: 'Optionaler Anzeigename fuer den Mandanten.',
      domainLabel: 'Domain',
      domainHint: 'Vollqualifizierter Domainname.',
      adminEmailLabel: 'Admin-E-Mail',
      adminEmailHint: 'E-Mail-Adresse des Mandanten-Administrators.',
      editionLabel: 'Edition',
      editionCommunity: 'Community Edition (CE)',
      editionEnterprise: 'Enterprise Edition (EE)',
      immutable: 'Fest',
      immutableInfoLabel: 'Warum nicht änderbar',
      immutableInfo: 'Domain und Edition lassen sich nach dem Anlegen nicht mehr ändern, der Admission-Webhook lehnt jede Änderung ab.',
    },
    apps: {
      desc: 'Aktivierte Anwendungen fuer diesen Mandanten.',
      releaseUnknownLabel: 'Warum ist die Auswahl gesperrt?',
      releaseUnknown: 'Das Release dieses Mandanten ist noch nicht bekannt. Die Auswahl ist bis zur ersten Bereitstellung gesperrt.',
    },
    mail: {
      title: 'SMTP-Konfiguration',
      smtpHost: 'SMTP-Host',
      smtpPort: 'SMTP-Port',
      credentialRef: 'Credential-Secret',
      dkimSelector: 'DKIM-Selektor',
    },
    iam: {
      title: 'Identity und Access Management',
      ldapOU: 'LDAP OU',
    },
    network: {
      title: 'Netzwerk-Isolierung',
      toggleLabel: 'NetworkPolicy aktivieren',
      toggleAriaLabel: 'NetworkPolicy aktivieren oder deaktivieren',
      desc: 'Schraenkt den Netzwerkverkehr zwischen Pods auf erlaubte Verbindungen ein.',
    },
    storage: {
      title: 'Speicher-Quota',
      quotaLabel: 'Speicher-Quota',
      quotaHint: 'z.B. 100Gi, 500Gi',
      desc: 'S3-Endpunkte koennen derzeit nur ueber die CR direkt konfiguriert werden.',
    },
    quota: {
      title: 'Ressourcen-Quota',
      cpuLabel: 'CPU Kerne',
      memoryLabel: 'Arbeitsspeicher (GiB)',
      podLabel: 'Max. Pods',
    },
    footer: {
      cancel: 'Abbrechen',
      save: 'Speichern',
    },
    confirm: {
      title: 'Aenderungen speichern?',
      confirmLabel: 'Ja, speichern',
      appliedPrefix: 'werden auf den Mandanten',
      appliedSuffix: ' angewendet. Dies kann eine Neuabstimmung (Reconcile) ausloesen.',
    },
  },
  componentPanel: {
    close: 'Panel schliessen',
    tabsAriaLabel: 'Komponenten-Tabs',
    tabs: {
      values: 'Aktuelle Values',
      diff: 'Diff',
      history: 'Historie',
    },
    loading: 'Lade Komponentendetails...',
    loadDetailError: 'Laden der Komponentendetails fehlgeschlagen',
    saveFailed: 'Speichern fehlgeschlagen',
    errorPrefix: 'Fehler:',
    values: {
      layerL1: 'L1 Upstream',
      layerL2: 'L2 Operator',
      layerL3: 'L3 Mandant',
      edit: 'Bearbeiten',
      save: 'Helm Upgrade ausloesen',
      cancel: 'Abbrechen',
      noData: 'Keine Daten verfuegbar.',
      noValues: 'Keine generierten Values gefunden. Die Komponente hat moeglicherweise noch keine Values erzeugt.',
      yamlParseErrorPrefix: 'YAML-Parsefehler:',
      yamlMustBeObject: 'YAML muss ein Objekt sein (kein Array, kein Skalar).',
    },
    diff: {
      noData: 'Keine Daten verfuegbar.',
      headerEditing: 'Diff: Gespeicherte Overrides → Bearbeitung',
      loading: 'Lade Diff …',
      loadFailedFallback: 'Diff laden fehlgeschlagen',
      loadErrorPrefix: 'Diff konnte nicht geladen werden:',
      noPrevious: 'Keine vorherige Helm-Revision vorhanden. Diff wird nach dem ersten Upgrade verfuegbar.',
      revisionPrefix: 'Revision',
      previousRevisionFallback: 'vorherige Revision',
      headerArrowCurrent: 'aktuell',
      headerPrefix: 'Diff:',
      diffLabel: 'Diff-Ansicht',
      noChanges: 'Keine Aenderungen.',
    },
    history: {
      empty: 'Keine Deploy-Historie verfuegbar.',
      ariaLabel: 'Deploy-Historie',
      revisionAriaLabelPrefix: 'Revision',
    },
  },
} as const;

export const en = {
  tenantEdit: {
    notFound: 'Tenant not found.',
    loadError: 'Failed to load.',
    saveError: 'Failed to save.',
    loading: 'Loading tenant data...',
    titlePrefix: 'Edit tenant:',
    changeSingular: 'change',
    changePlural: 'changes',
    close: 'Close',
    tabsAriaLabel: 'Tenant settings',
    tabs: {
      identity: 'Identity',
      apps: 'Apps',
      mail: 'Mail',
      iam: 'IAM',
      network: 'Network',
      storage: 'Storage',
      quota: 'Quota',
    },
    identity: {
      displayNameLabel: 'Display name',
      displayNameHint: 'Optional display name for the tenant.',
      domainLabel: 'Domain',
      domainHint: 'Fully qualified domain name.',
      adminEmailLabel: 'Admin email',
      adminEmailHint: 'Email address of the tenant administrator.',
      editionLabel: 'Edition',
      editionCommunity: 'Community Edition (CE)',
      editionEnterprise: 'Enterprise Edition (EE)',
      immutable: 'Fixed',
      immutableInfoLabel: 'Why this cannot change',
      immutableInfo: 'Domain and edition cannot change after creation, the admission webhook rejects any change.',
    },
    apps: {
      desc: 'Enabled apps for this tenant.',
      releaseUnknownLabel: 'Why is the selection locked?',
      releaseUnknown: 'This tenant has no known release yet. App selection is locked until its first deployment.',
    },
    mail: {
      title: 'SMTP configuration',
      smtpHost: 'SMTP host',
      smtpPort: 'SMTP port',
      credentialRef: 'Credential secret',
      dkimSelector: 'DKIM selector',
    },
    iam: {
      title: 'Identity and access management',
      ldapOU: 'LDAP OU',
    },
    network: {
      title: 'Network isolation',
      toggleLabel: 'Enable NetworkPolicy',
      toggleAriaLabel: 'Enable or disable NetworkPolicy',
      desc: 'Restricts pod-to-pod network traffic to allowed connections.',
    },
    storage: {
      title: 'Storage quota',
      quotaLabel: 'Storage quota',
      quotaHint: 'e.g. 100Gi, 500Gi',
      desc: 'S3 endpoints can currently only be configured directly on the CR.',
    },
    quota: {
      title: 'Resource quota',
      cpuLabel: 'CPU cores',
      memoryLabel: 'Memory (GiB)',
      podLabel: 'Max pods',
    },
    footer: {
      cancel: 'Cancel',
      save: 'Save',
    },
    confirm: {
      title: 'Save changes?',
      confirmLabel: 'Yes, save',
      appliedPrefix: 'will be applied to tenant',
      appliedSuffix: '. This may trigger a reconcile.',
    },
  },
  componentPanel: {
    close: 'Close panel',
    tabsAriaLabel: 'Component tabs',
    tabs: {
      values: 'Current values',
      diff: 'Diff',
      history: 'History',
    },
    loading: 'Loading component details...',
    loadDetailError: 'Failed to load component detail',
    saveFailed: 'Failed to save',
    errorPrefix: 'Error:',
    values: {
      layerL1: 'L1 Upstream',
      layerL2: 'L2 Operator',
      layerL3: 'L3 Tenant',
      edit: 'Edit',
      save: 'Trigger Helm upgrade',
      cancel: 'Cancel',
      noData: 'No data available.',
      noValues: 'No generated values found. The component may not have generated values yet.',
      yamlParseErrorPrefix: 'YAML parse error:',
      yamlMustBeObject: 'YAML must be an object (no array, no scalar).',
    },
    diff: {
      noData: 'No data available.',
      headerEditing: 'Diff: stored overrides → edit',
      loading: 'Loading diff …',
      loadFailedFallback: 'Failed to load diff',
      loadErrorPrefix: 'Failed to load diff:',
      noPrevious: 'No previous Helm revision yet. The diff becomes available after the first upgrade.',
      revisionPrefix: 'Revision',
      previousRevisionFallback: 'previous revision',
      headerArrowCurrent: 'current',
      headerPrefix: 'Diff:',
      diffLabel: 'Diff view',
      noChanges: 'No changes.',
    },
    history: {
      empty: 'No deploy history available.',
      ariaLabel: 'Deploy history',
      revisionAriaLabelPrefix: 'Revision',
    },
  },
} as const;
